// Tries — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as trees.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import trieDrills from './trieDrills';
const drill = id => trieDrills.find(d => d.id === id);

const tries = {
  key: 'Tries',
  tagline: 'A tree built for strings — where every shared prefix is stored exactly once, no matter how many words use it.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Tries',
      type: 'text',
      body: [
        "A trie (pronounced \"try,\" from reTRIEval) is a tree specialized for storing strings, where each edge represents one character and each path from the root spells out a prefix. Unlike a binary tree, a trie node can branch into many children at once — one per possible next character, not just two.",
        "The defining property: any two words sharing a common prefix also share the exact same nodes for that prefix. Insert \"car\" and \"card\" into a trie, and \"card\" doesn't get its own separate c-a-r-d chain — it reuses the exact same c, a, r nodes \"car\" already created, and just adds one more node, d, onto the end.",
      ],
    },
    {
      id: 'why',
      title: 'Why Tries?',
      type: 'text',
      body: [
        "Storing a dictionary of words in a HashSet<String> answers \"does this exact word exist?\" in O(1) — but it CANNOT efficiently answer \"does anything start with this prefix?\" without checking every single stored word one by one. A trie answers both questions in time proportional only to the length of the word or prefix itself, completely independent of how many other words are stored.",
        "This is exactly the shape behind autocomplete, spell-checkers, and IP routing tables: all of them need \"find everything matching this prefix,\" fast, out of a potentially enormous dictionary — and a trie's shared-prefix structure is built precisely for that.",
        "The second major use, less obvious at first: a trie doesn't have to be built from characters at all. A BINARY trie — built from the individual BITS of numbers — is the standard technique behind an entire family of \"maximize/minimize XOR\" problems, generalizing the same prefix-sharing idea to numbers instead of strings.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Tries Work — A Reception Desk Sign-In Sheet',
      type: 'text',
      heading: 'One Shared Path Per Prefix, Branching Only Where Names Actually Differ',
      body: [
        "Picture a hotel's paper sign-in log, but organized cleverly: instead of one line per guest, it's organized letter by letter. Every guest whose name starts with \"C\" is grouped under one \"C\" heading. Under that, everyone whose name continues \"Ca\" is grouped further, and so on — \"Carter\" and \"Carlos\" share the \"Car\" heading and only split apart at the fourth letter. To check whether anyone named \"Carlos\" signed in, you don't scan every name on the page — you follow C, then a, then r, then l, and either the trail continues to a marked \"complete name\" or it doesn't.",
        "This is exactly how a trie stores words: shared prefixes are stored exactly once, and the structure only branches at the point where two words actually start to differ. The deeper two words share a prefix, the more of their path through the trie is literally the same nodes.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Trie',
      type: 'trie-diagram',
      body: [
        "The root represents an empty prefix — nothing has been typed yet. Each edge below it is labeled with one character, and a small green dot marks any node where a complete word ends (a node can both be a complete word AND have children continuing to longer words, the way \"car\" here is complete but also continues on to \"card\" and \"care\").",
        "Notice how \"cat,\" \"car,\" \"card,\" and \"care\" all share the same c → a path, splitting only at the third letter. That shared structure is the entire point.",
      ],
      demo: {
        trie: {
          children: {
            c: { children: { a: { children: {
              t: { children: {}, isEnd: true },
              r: { children: {
                d: { children: {}, isEnd: true },
                e: { children: {}, isEnd: true },
              }, isEnd: true },
            }, isEnd: false } }, isEnd: false },
            d: { children: { o: { children: { g: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
          }, isEnd: false,
        },
        note: 'Words stored: cat, car, card, care, dog. "car" is itself a complete word (green dot) AND a prefix of two longer words.',
      },
    },
    {
      id: 'declare',
      title: 'The TrieNode Class and Core Operations',
      type: 'code',
      code:
`class TrieNode {
    Map<Character, TrieNode> children = new HashMap<>();
    boolean isEnd = false;
}

class Trie {
    private TrieNode root = new TrieNode();

    public void insert(String word) {
        TrieNode curr = root;
        for (char c : word.toCharArray()) {
            curr = curr.children.computeIfAbsent(c, k -> new TrieNode());
        }
        curr.isEnd = true;
    }

    public boolean search(String word) {
        TrieNode node = walk(word);
        return node != null && node.isEnd;
    }

    public boolean startsWith(String prefix) {
        return walk(prefix) != null;
    }

    private TrieNode walk(String s) {
        TrieNode curr = root;
        for (char c : s.toCharArray()) {
            curr = curr.children.get(c);
            if (curr == null) return null;
        }
        return curr;
    }
}`,
      note: 'search() and startsWith() share almost identical code — the only difference is search() additionally checks node.isEnd, since reaching a node just means the CHARACTERS exist, not that a word actually ends there.',
    },
    {
      id: 'insertion',
      title: 'Why Insertion Reuses Existing Nodes',
      type: 'code',
      code:
`// Inserting "car" creates 3 new nodes: c -> a -> r (marked isEnd)
trie.insert("car");

// Inserting "card" reuses the EXISTING c, a, r nodes,
// and only creates ONE new node: d (marked isEnd)
trie.insert("card");

// The 'r' node from "car" is now BOTH isEnd=true (a complete word)
// AND has a child 'd' continuing to a longer word`,
      note: 'computeIfAbsent is what makes this automatic: if a child for that character already exists (because an earlier word created it), it\'s reused as-is; a new node is only created the first time a particular character is needed at that position.',
    },
    {
      id: 'wildcard',
      title: 'Searching With Wildcards (DFS Branching)',
      type: 'code',
      code:
`boolean search(TrieNode node, String pattern, int i) {
    if (i == pattern.length()) return node.isEnd;
    char c = pattern.charAt(i);

    if (c == '.') {
        for (TrieNode child : node.children.values()) {
            if (search(child, pattern, i + 1)) return true;   // try EVERY child
        }
        return false;
    }
    TrieNode child = node.children.get(c);
    return child != null && search(child, pattern, i + 1);
}`,
      note: 'This is the one place a trie search stops being a single straight-line walk — a wildcard character forces trying every branch at that level, turning the search into a small DFS instead of a direct path.',
    },
    {
      id: 'binarytrie',
      title: 'A Trie Built From Bits, Not Characters',
      type: 'code',
      code:
`// A trie with only two possible "letters" at every level: 0 and 1
void insertBits(TrieNode root, int num) {
    TrieNode node = root;
    for (int bit = 31; bit >= 0; bit--) {           // most significant bit first
        int b = (num >> bit) & 1;
        node.children.putIfAbsent(b, new TrieNode());
        node = node.children.get(b);
    }
}`,
      note: 'This is the same exact trie structure, just with a 2-symbol alphabet instead of 26+ letters — it\'s what powers the "maximum XOR of two numbers" family of problems, where greedily choosing the opposite bit at every level maximizes the result.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Confusing "the path exists" with "a word ends here"',
          why: 'startsWith("car") being true does NOT mean "car" was itself inserted as a complete word — it might only exist as a prefix of "card." Checking node != null alone (instead of node.isEnd) after a walk incorrectly reports a match for search().',
          fix: 'search() must check BOTH that the walk succeeded (node != null) AND that node.isEnd is true. startsWith() only needs the former.',
        },
        {
          mistake: 'Forgetting that a node can be both isEnd AND have children',
          why: 'A common bug assumes a "complete word" node must be a leaf with no children — but "car" being a complete word doesn\'t stop "card" and "care" from continuing past it. Code that returns early the moment isEnd is true (instead of continuing to check children when needed) breaks prefix-based operations.',
          fix: 'Never treat isEnd as mutually exclusive with having children — check both properties independently for whatever the operation actually needs.',
        },
        {
          mistake: 'Using a fixed-size array for an unknown or large alphabet',
          why: 'TrieNode[26] only works for lowercase English letters — inserting digits, uppercase letters, or Unicode characters with that fixed array either crashes (index out of bounds) or silently corrupts data.',
          fix: 'Use Map<Character, TrieNode> whenever the alphabet isn\'t a small, fixed, known set — reserve the array optimization for when the alphabet is genuinely constrained.',
        },
        {
          mistake: 'Rebuilding the whole trie unnecessarily for every query',
          why: 'Building the trie is the expensive one-time setup cost (O(total characters)) — rebuilding it for every single search or prefix check throws away the entire point of using a trie instead of just scanning a list.',
          fix: 'Build the trie once, up front, and reuse it for every subsequent query — this amortized setup cost is exactly what makes a trie worth using at all.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'insert(word)', time: 'O(L) — L = word length', note: 'One step per character, regardless of how many other words are already stored.' },
        { op: 'search(word) / startsWith(prefix)', time: 'O(L)', note: 'A direct walk down existing nodes — never touches unrelated branches.' },
        { op: 'Wildcard search with k \'.\' characters', time: 'O(alphabet^k · L) worst case', note: 'Each wildcard can branch into every child — still far better than checking every stored word individually.' },
        { op: 'Space, overall', time: 'O(total characters across all inserted words)', note: 'Bounded by shared prefixes — far less than storing every word\'s full characters separately when prefixes overlap heavily.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for a trie when you see…',
      body: [
        'Autocomplete, spell-check, or "find every word starting with this prefix" over a large dictionary.',
        'Repeated searches against the SAME fixed set of words or patterns — the one-time trie-build cost pays off across many queries.',
        'A wildcard character search where a plain HashSet can\'t help, since it needs exact string matches.',
        '"Maximize/minimize XOR" problems over a set of numbers — a binary trie of bit patterns, not a character trie.',
        'Avoid it for a small, one-off set of words where a simple HashSet already answers every question the problem actually asks — a trie adds real implementation complexity that only pays off with prefix-based queries or repeated lookups.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('build-trie-node'),
            drill('count-words-with-prefix'),
            drill('trie-search-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('longest-common-prefix-using-trie'),
            drill('wildcard-search-drill'),
            drill('autocomplete-suggestions'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('replace-words-with-root'),
            drill('word-search-ii-drill'),
            drill('maximum-xor-of-two-numbers'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with trie construction and prefix-based traversal before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Shared prefixes reuse the exact same nodes — that\'s the entire reason a trie exists.',
        'search() checks node != null AND node.isEnd. startsWith() only needs node != null.',
        'A node can be isEnd=true AND still have children — "car" being complete doesn\'t block "card" from continuing.',
        'insert/search/startsWith are all O(word length), completely independent of how many other words are stored.',
        'A wildcard character forces trying every child at that level — a small DFS instead of one straight walk.',
        'A trie doesn\'t require characters — a BINARY trie of bit patterns powers "maximum XOR" problems the same way.',
        'Build the trie once, reuse it for every query — the setup cost is what the whole pattern is designed to amortize.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default tries;
